<template>
    <div>
        <v-card>
            <v-card-title>
                <v-row justify="space-between">
                    <v-col cols="2">Edit Experiment Metadata for {{ experiment.code }}</v-col>
                    <v-spacer />
                    <v-col v-if="showDownloads" cols="10" class="text-right">
                        <v-btn v-on:click="createRandomizationTemplate()" color="info">
                            Randomization
                            <v-icon end>$download</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-container fluid>
                <v-row>
                    <v-col>
                        <v-form v-model="isFormValid">
                            <v-row>
                                <v-col cols="8">
                                    <v-text-field
                                        v-model="experimentDisplayName"
                                        :label="`Experiment Name`"
                                        class="pl-4 mr-2"
                                        :rules="[value => !!value || 'Experiment name is required']"
                                        :disabled="!canManageMetadata"
                                    >
                                    </v-text-field>
                                </v-col>
                                <v-col cols="4">
                                    <v-autocomplete
                                        v-model="experimentGroup"
                                        :items="experimentGroups"
                                        item-title="name"
                                        item-value="id"
                                        label="Experiment Group"
                                        clearable
                                        class="mr-4"
                                        :disabled="!canReadExperimentGroups || !canManageMetadata"
                                        :rules="[value => !!value || 'Experiment group is required']"
                                    >
                                    </v-autocomplete>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="4">
                                    <v-text-field
                                        type="date"
                                        v-model="startDate"
                                        label="Day 0"
                                        class="pl-4"
                                        :rules="[value => !!value || 'Day 0 is required']"
                                        :disabled="!canManageMetadata"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="4">
                                    <v-autocomplete
                                        v-model="status"
                                        :items="[
                                            'Ongoing',
                                            'Completed',
                                            'Not started',
                                            'Canceled',
                                            'Blocked',
                                        ]"
                                        label="Experiment Status"
                                        hide-details
                                        clearable
                                        :disabled="!canManageMetadata"
                                    >
                                    </v-autocomplete>
                                </v-col>
                                <v-col cols="4">
                                    <v-autocomplete
                                        v-model="qcStatus"
                                        :items="['Pass', 'Fail']"
                                        label="QC Status"
                                        hide-details
                                        clearable
                                        class="mr-4"
                                        :disabled="!canManageMetadata"
                                    >
                                    </v-autocomplete>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="4">
                                    <v-text-field
                                        v-model="alternativeIdentifier"
                                        label="Alternative Identifier"
                                        class="pl-4 mr-2"
                                        :disabled="!canManageMetadata"
                                    >
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <experiment-metadata-template-fields
                                        v-if="experimentMetadata"
                                        ref="experimentMetadataFields"
                                        metadata-context="experiment"
                                        v-model="experimentMetadata"
                                        :include-geo-coordinate-fields="true"
                                        :disabled="!canManageMetadata"
                                    />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="4">
                                    <v-checkbox
                                        v-model="isUsingMeasurements"
                                        label="Use Measurements"
                                        :disabled="!canManageMetadata"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-col>
                </v-row>
            </v-container>
            <v-card-actions v-if="canUpdateExperiments">
                <v-spacer />
                <v-btn color="primary" @click="save" :disabled="!isFormValid || !canManageMetadata">Update Metadata</v-btn>
                <v-btn color="warning" @click="cancel">Cancel</v-btn>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useAuth } from "@/lib/ts/auth";
import config from "../../config";
import {
    experimentGroupsAPI,
    ExperimentGroupDTO,
} from "@/services/experimentGroups/experimentGroupsAPI";
import {
    ExperimentListDTO,
    ExperimentQCStatus,
    experimentsAPI,
    ExperimentStatus,
} from "@/services/experiments/experimentsAPI";
import ExperimentMetadataTemplateFields from "@/components/Experiments/ExperimentMetadataTemplateFields.vue";
import { withLoadingScreen } from "@/lib/loading";
import { DateTime } from "luxon";

const props = withDefaults(
    defineProps<{
        showDownloads?: boolean;
        experiment: ExperimentListDTO;
    }>(),
    {
        showDownloads: false,
    }
);

const emit = defineEmits<{
    (e: "cancel"): void;
    (e: "metadataUpdated"): void;
}>();

const auth = useAuth();
const experimentDisplayName = ref("");
const experimentGroup = ref("");
const experimentGroups = ref<ExperimentGroupDTO[]>([]);
const status = ref<ExperimentStatus | null>(null);
const qcStatus = ref<ExperimentQCStatus | null>(null);
const isUsingMeasurements = ref(false);
const experimentMetadata = ref<Record<string, any>>(null as unknown as Record<string, any>);
const experimentMetadataFields = ref();
const startDate = ref<string | null>(DateTime.fromISO(props.experiment.startDate, { zone: "utc" }).toISODate());
const alternativeIdentifier = ref<string | null>(null);
const isFormValid = ref(false);

onMounted(async () => {
    resetExperimentFields();
    await withLoadingScreen(async () => {
        experimentGroups.value = await experimentGroupsAPI.fetchExperimentGroups();
        experimentMetadata.value = await experimentsAPI.getMetadataForExperiment(
            props.experiment.code
        );
    });
});

const canReadExperimentGroups = computed(() => {
    return auth.isAuthorized("ExperimentGroups:Read");
});

const canUpdateExperiments = computed(() => {
    return auth.isAuthorized("Experiments:Update");
});

const canManageMetadata = computed(() => {
    return auth.isAuthorized("Experiments:ManageV2Metadata");
});

function createRandomizationTemplate() {
    const method = "plotlayout";
    const code = props.experiment.code;
    window.open(
        `${config.apiHost}/experiment/${code}/excel/fieldData/${method}/${code}_Randomization.xlsx`,
        "_blank"
    );
}

async function save() {
    await saveExperimentData();
}

function cancel() {
    resetExperimentFields();
    experimentMetadataFields.value?.reset();
    emit("cancel");
}

function resetExperimentFields() {
    startDate.value = DateTime.fromISO(props.experiment.startDate!, { zone: "utc" }).toISODate();
    isUsingMeasurements.value = props.experiment.isUsingMeasurements;
    experimentDisplayName.value = props.experiment.displayName || props.experiment.name;
    experimentGroup.value = props.experiment.experimentGroup;
    status.value = props.experiment.status as ExperimentStatus | null;
    qcStatus.value = props.experiment.qcStatus as ExperimentQCStatus | null;
    alternativeIdentifier.value = props.experiment.alternativeIdentifier || null;
}

async function saveExperimentData() {
    await withLoadingScreen(async () => {
        await experimentsAPI.updateExperiment(props.experiment.code, {
            displayName: experimentDisplayName.value,
            alternativeIdentifier: alternativeIdentifier.value,
            startDate: startDate.value!,
            experimentGroupId: experimentGroup.value,
            status: status.value as ExperimentStatus | null,
            qcStatus: qcStatus.value as ExperimentQCStatus | null,
            isUsingMeasurements: isUsingMeasurements.value,
            metadata: experimentMetadata.value,
        });
    });
    emit("metadataUpdated");
}
</script>
